import type { Uuid } from '@sibe/utilities/common';
import type { HsGeometry } from '../../graphic-engine-new/geometry/geometry.interface';
import { HsVector3 } from '../../math-new/objects/vector3.class';
import type { HsGraphics } from '../graphics.class';
import type { HsMaterial } from '../materials/material.class';
import type { HsGraphicsBuffer } from '../types/graphic-buffer.type';
import { HsGraphicObject } from './graphic-object.class';

/**
 * Handles s graphic collection.
 */
export class HsGraphicCollection extends HsGraphicObject {
  private objectsCount: number = 0;

  public matrixBuffer: HsGraphicsBuffer | null = null;
  public colorBuffer: HsGraphicsBuffer | null = null;

  private matrixArray: number[] = [];
  private colorArray: number[] = [];
  private hiddenInstances: Set<number> = new Set();

  constructor(uuid: Uuid, name: string, geometry: HsGeometry, material: HsMaterial) {
    super(uuid, name, geometry, material);
  }

  public addObject(object: HsGraphicObject): void {
    this.addInstance(object.worldMatrix.values, object.material.color.values);
  }

  public addInstance(matrix: readonly number[], color: readonly number[]): void {
    this.objectsCount++;
    this.matrixArray.push(...matrix);
    this.colorArray.push(...color, 1);
  }

  public updateBuffers(graphics: HsGraphics): void {
    this.matrixBuffer = graphics.createBuffer(this.drawnMatrices());
    this.colorBuffer = graphics.createBuffer(new Float32Array(this.colorArray));
  }

  /** Whether the instance at `index` is left out of drawing and picking (`setHiddenInstances`). */
  public isInstanceHidden(index: number): boolean {
    return this.hiddenInstances.has(index);
  }

  /** Instances drawn: all but the hidden ones. */
  public getVisibleCount(): number {
    return this.objectsCount - this.hiddenInstances.size;
  }

  /**
   * Hides exactly the instances in `hidden` and shows the others. Hidden instances get a zero matrix in the
   * GPU buffer, so drawing and GPU picking skip them while the batch, its geometry and the CPU-side
   * transforms stay as they are: one instance disappears without rebuilding the batch. Returns whether
   * anything changed.
   */
  public setHiddenInstances(hidden: ReadonlySet<number>, graphics: HsGraphics): boolean {
    const next: Set<number> = new Set();
    hidden.forEach((index: number) => {
      if (Number.isInteger(index) && index >= 0 && index < this.objectsCount) {
        next.add(index);
      }
    });
    if (
      next.size === this.hiddenInstances.size &&
      [...next].every((index: number) => this.hiddenInstances.has(index))
    ) {
      return false;
    }
    this.hiddenInstances = next;
    if (this.matrixBuffer !== null) {
      graphics.deleteBuffer(this.matrixBuffer);
      this.matrixBuffer = graphics.createBuffer(this.drawnMatrices());
    }
    return true;
  }

  public updateCenter(): void {
    if (this.objectsCount === 0) {
      return;
    }

    const center: HsVector3 = HsVector3.makeZeroVector();
    const tempVector: HsVector3 = HsVector3.makeZeroVector();
    for (let i: number = 0; i < this.objectsCount; i++) {
      tempVector.set(this.matrixArray[i * 16 + 12], this.matrixArray[i * 16 + 13], this.matrixArray[i * 16 + 14]);
      center.add(tempVector);
    }
    center.divideByScalar(this.objectsCount);
    this.worldMatrix.setPosition(center.values);
  }

  public updateBoundingSphere(): void {
    if (this.objectsCount === 0) {
      this.boundingSphere.radius = 0;
      return;
    }

    let radius: number = 0;
    const geometryRadius: number = this.geometry.boundingSphere.radius;
    const geometryCenter: HsVector3 = this.geometry.boundingSphere.center;
    const tempPosition1: HsVector3 = HsVector3.makeZeroVector();
    const tempPosition2: HsVector3 = HsVector3.makeZeroVector();
    tempPosition1.setFromMatrixPosition(this.worldMatrix.values);
    for (let i: number = 0; i < this.objectsCount; i++) {
      const matrixOffset: number = i * 16;
      tempPosition2.set(
        this.matrixArray[matrixOffset] * geometryCenter.x +
          this.matrixArray[matrixOffset + 4] * geometryCenter.y +
          this.matrixArray[matrixOffset + 8] * geometryCenter.z +
          this.matrixArray[matrixOffset + 12],
        this.matrixArray[matrixOffset + 1] * geometryCenter.x +
          this.matrixArray[matrixOffset + 5] * geometryCenter.y +
          this.matrixArray[matrixOffset + 9] * geometryCenter.z +
          this.matrixArray[matrixOffset + 13],
        this.matrixArray[matrixOffset + 2] * geometryCenter.x +
          this.matrixArray[matrixOffset + 6] * geometryCenter.y +
          this.matrixArray[matrixOffset + 10] * geometryCenter.z +
          this.matrixArray[matrixOffset + 14]
      );
      const scaleX: number = Math.hypot(
        this.matrixArray[matrixOffset] ?? 0,
        this.matrixArray[matrixOffset + 1] ?? 0,
        this.matrixArray[matrixOffset + 2] ?? 0
      );
      const scaleY: number = Math.hypot(
        this.matrixArray[matrixOffset + 4] ?? 0,
        this.matrixArray[matrixOffset + 5] ?? 0,
        this.matrixArray[matrixOffset + 6] ?? 0
      );
      const scaleZ: number = Math.hypot(
        this.matrixArray[matrixOffset + 8] ?? 0,
        this.matrixArray[matrixOffset + 9] ?? 0,
        this.matrixArray[matrixOffset + 10] ?? 0
      );
      const maximumScale: number = Math.max(scaleX, scaleY, scaleZ, 1);
      radius = Math.max(radius, HsVector3.distance(tempPosition1, tempPosition2) + geometryRadius * maximumScale);
    }
    this.boundingSphere.radius = radius;
    this.boundingSphere.center.copy(tempPosition1);
    this.boundingBox.min.set(
      tempPosition1.x - this.boundingSphere.radius,
      tempPosition1.y - this.boundingSphere.radius,
      tempPosition1.z - this.boundingSphere.radius
    );
    this.boundingBox.max.set(
      tempPosition1.x + this.boundingSphere.radius,
      tempPosition1.y + this.boundingSphere.radius,
      tempPosition1.z + this.boundingSphere.radius
    );
  }

  public getCount(): number {
    return this.objectsCount;
  }

  /** The world matrix (column-major) of the instance at `index`, or null outside the collection. */
  public getInstanceMatrix(index: number): number[] | null {
    if (!Number.isInteger(index) || index < 0 || index >= this.objectsCount) {
      return null;
    }
    return this.matrixArray.slice(index * 16, index * 16 + 16);
  }

  /**
   * Draws the listed instances (index to rgb) in their own colors, such as a selection highlight, until the next
   * `updateBuffers`; an empty map restores the instance colors. Only the color buffer is replaced.
   */
  public setInstanceColorOverrides(overrides: ReadonlyMap<number, readonly number[]>, graphics: HsGraphics): void {
    if (this.colorBuffer === null) {
      return;
    }
    const colors: Float32Array = new Float32Array(this.colorArray);
    overrides.forEach((rgb: readonly number[], index: number) => {
      if (Number.isInteger(index) && index >= 0 && index < this.objectsCount) {
        colors.set([rgb[0], rgb[1], rgb[2], 1], index * 4);
      }
    });
    graphics.deleteBuffer(this.colorBuffer);
    this.colorBuffer = graphics.createBuffer(colors);
  }

  public clear(graphics: HsGraphics): void {
    this.objectsCount = 0;
    this.matrixArray = [];
    this.colorArray = [];
    this.hiddenInstances = new Set();
    if (this.matrixBuffer !== null) {
      graphics.deleteBuffer(this.matrixBuffer);
    }
    if (this.colorBuffer !== null) {
      graphics.deleteBuffer(this.colorBuffer);
    }
    this.matrixBuffer = null;
    this.colorBuffer = null;
  }

  private drawnMatrices(): Float32Array {
    const matrices: Float32Array = new Float32Array(this.matrixArray);
    this.hiddenInstances.forEach((index: number) => matrices.fill(0, index * 16, index * 16 + 16));
    return matrices;
  }
}
